import React, { useState, useEffect } from "react";
import { Menu, MenuItem, IconButton, Typography, Box, Divider } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BankDetailsModal from "./BankDetailsModal";
import UpdateMembershipModal from "./UpdateMembershipModal";
import SendMessageModal from "./SendMessageModal";
import UserAssetsModal from "./UserAssetsModal";
import { useNavigate } from "react-router-dom";
import EmergencyContactsModal from "./EmergencyContactsModal";
import CompoundDetiles from "./CompoundDetiles";
import apiClient from "./apiClient";
import DeleteUserModal from "../components/DeleteUserModal";
import socketService from "../socket/newsocketService";

// Legacy Firebase implementation kept during the backend migration:
// import { getDatabase, ref, get, update, remove } from "firebase/database";
// import socketService from "socket/SocketService";

const menuIcons = {
  payment: require("../assets/clock.png"),
  address: require("../assets/home.png"),
  verified: require("../assets/check-mark.png"),
  suspend: require("../assets/pause.png"),
  reset: require("../assets/user.png"),
  summary: require("../assets/credit-card.png"),
  trip: require("../assets/car.png"),
  contact: require("../assets/info.png"),
  devices: require("../assets/link.png"),
  logs: require("../assets/comment.png"),
  flag: require("../assets/flags.png"),
  delete: require("../assets/delete.png"),
};

export default function LongMenuButton({ userId, userDeleted, userData, updateMemebrshipCheck }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuVisible, setSubmenuVisible] = useState(false);
  const [verified, setVerified] = useState(Boolean(userData?.verifiedUser));
  const [verifiedAddress, setVerifiedAddress] = useState(Boolean(userData?.addressVerified));
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [membershipModalOpen, setMembershipModalOpen] = useState(false);
  const [openAssetsModal, setOpenAssetsModal] = useState(false);
  const [openEmergencyModal, setOpenEmergencyModal] = useState(false);
  const [openCompoundModal, setOpenCompoundModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const navigate = useNavigate();

  const currentAdmin = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();
  const canDelete =
    Boolean(currentAdmin?.superAdmin) ||
    currentAdmin?.role === "superAdmin" ||
    currentAdmin?.role === "admin" ||
    currentAdmin?.canDelete === true ||
    currentAdmin?.permissions?.canDelete === true;


  const [openSendMessage, setOpenMessage] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchUserStatus = async () => {
      try {
        const response = await apiClient.get(`/users/${userId}`);
        const data = response?.data || {};
        setVerified(Boolean(data?.verifiedUser));
        setVerifiedAddress(Boolean(data?.addressVerified));
      } catch (error) {
        //  console.error("Error fetching user status:", error);
      }
    };
    if (userId) {
      fetchUserStatus();
    }
  }, [userId]);

  useEffect(() => {
    setVerified(Boolean(userData?.verifiedUser));
    setVerifiedAddress(Boolean(userData?.addressVerified));
  }, [userData]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setSubmenuVisible(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSubmenuVisible(false);
  };

  const handleDeleteUser = async () => {
    try {
      // Legacy Firebase delete kept for reference:
      // await remove(ref(db, `users/${userId}`));
      await apiClient.delete(`/users/${userId}`);
      await userDeleted();
      toast.success("User deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete user.");
    }
  };

  const handleToggleVerify = async () => {
    try {
      const newStatus = !verified;
      const updates = { verifiedUser: newStatus };
      if (newStatus) updates.escortCount = 2;

      // Legacy Firebase update kept for reference:
      // await update(ref(db, `users/${userId}`), updates);
      await apiClient.put(`/users/${userId}`, updates);
      if (newStatus) {
        socketService.emit("sendcustompushnotification", {
          userId: userId,
          title: "NYSC Verification Complete",
          message:
            "Your NYSC ID has been successfully verified. Now you have access to free escort service provide by karzame.",
          action: "NYSC Verification",
        });
      }

      setVerified(newStatus);
      toast.success(`User marked as ${newStatus ? "Verified" : "Unverified"}`);
    } catch (error) {
      toast.error("Failed to update verification status.");
    }
  };

  const handleAddressVerification = async () => {
    try {
      const newStatus = !verifiedAddress;
      // Legacy Firebase update kept for reference:
      // const userRef = ref(db, `users/${userId}`);
      // const snapshot = await get(userRef);
      // if (snapshot.exists()) {
      //   const currentStatus = snapshot.val().addressVerified;
      //   const newStatus = !currentStatus;
      //   await update(userRef, { addressVerified: newStatus });
      // }
      await apiClient.put(`/users/${userId}/addressVerified`, {
        addressVerified: newStatus,
      });
      setVerifiedAddress(newStatus);
      toast.success(`Address ${newStatus ? "verified" : "unverified"} successfully`);
    } catch (error) {
      //  console.error("Failed to verify address:", error);
      toast.error("Failed to update address verification.");
    }
  };

  const handleOpenBankDetails = () => {
    setAnchorEl(null);
    setSubmenuVisible(false);
    setBankModalOpen(true);
  };

  const renderMenuItem = (icon, label, onClick = null) => (
    <MenuItem onClick={onClick} sx={{ display: "flex", gap: 2 }}>
      <img src={icon} alt="" width={15} height={15} />
      <span style={{ fontSize: 14 }}>{label}</span>
    </MenuItem>
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            padding: 0,
            display: "flex",
            flexDirection: "row-reverse",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            borderRadius: 8,
          },
        }}
      >
        <Box sx={{ p: 2, width: 250 }}>
          <Typography fontWeight={500} mb={1} color="black" fontSize="1.05rem">
            User Account Management
          </Typography>

          {renderMenuItem(menuIcons.payment, "Payment History")}
          {renderMenuItem(
            menuIcons.address,
            verifiedAddress ? "Mark Address as Unverified" : "Verify Home Address",
            handleAddressVerification
          )}
          {renderMenuItem(
            menuIcons.verified,
            verified ? "Mark as Unverified" : "Mark as Verified",
            handleToggleVerify
          )}

          {renderMenuItem(menuIcons.verified, "Update Membership", () =>
            setMembershipModalOpen(true)
          )}

          {renderMenuItem(menuIcons.verified, "Send Message", () => setOpenMessage(true))}
          {renderMenuItem(menuIcons.verified, "Registered Asset", () => setOpenAssetsModal(true))}
          {renderMenuItem(menuIcons.contact, "Emergency Contact", () =>
            setOpenEmergencyModal(true)
          )}
          {renderMenuItem(menuIcons.contact, "Registered Compound", () =>
            setOpenCompoundModal(true)
          )}

          <Divider sx={{ my: 1 }} />

          <MenuItem onClick={() => setSubmenuVisible(true)}>... Other Actions</MenuItem>
        </Box>

        {submenuVisible && (
          <Box
            sx={{
              p: 2,
              width: 250,
              backgroundColor: "#fff",
              borderRight: "1px solid #ccc",
            }}
          >
            <Typography fontWeight={500} mb={1} color="black" fontSize="1.05rem">
              Other Actions
            </Typography>
            {renderMenuItem(menuIcons.suspend, "Suspend Account")}
            {renderMenuItem(menuIcons.reset, "Reset Password")}
            {renderMenuItem(menuIcons.summary, "Transaction Summary")}
            {renderMenuItem(menuIcons.trip, "Escort Trip History", () =>
              navigate(`/UserEscortHistory/${userId}`)
            )}

            {renderMenuItem(menuIcons.devices, "Linked Devices")}
            {renderMenuItem(menuIcons.logs, "Chat Logs / Admin Notes")}
            {renderMenuItem(menuIcons.flag, "Flag User / Mark as Suspicious")}
            {canDelete && renderMenuItem(menuIcons.delete, "Delete Account", () => {
              setAnchorEl(null);
              setSubmenuVisible(false);
              setDeleteModalOpen(true);
            })}
            {renderMenuItem(menuIcons.summary, "Bank Details", handleOpenBankDetails)}
          </Box>
        )}
      </Menu>

      <BankDetailsModal
        open={bankModalOpen}
        onClose={() => setBankModalOpen(false)}
        paymentData={userData}
      />

      <UpdateMembershipModal
        open={membershipModalOpen}
        onClose={() => setMembershipModalOpen(false)}
        userId={userId}
        currentType={userData?.activesubscription?.type}
        currentSubscription={userData?.activesubscription}
        onMembershipUpdated={(updateMembership) => {
          if (updateMembership) updateMemebrshipCheck(true);
        }}
      />

      <SendMessageModal
        visible={openSendMessage}
        onClose={() => setOpenMessage(false)}
        userData={userData}
      />

      <UserAssetsModal
        open={openAssetsModal}
        onClose={() => setOpenAssetsModal(false)}
        userId={userId}
      />
      <EmergencyContactsModal
        open={openEmergencyModal}
        onClose={() => setOpenEmergencyModal(false)}
        userData={userData}
      />
      <CompoundDetiles
        open={openCompoundModal}
        onClose={() => setOpenCompoundModal(false)}
        userData={userData}
      />
      <DeleteUserModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        user={userData}
        onUserDeleted={() => {
          if (userDeleted) userDeleted();
        }}
      />
    </>
  );
}
